import { Realtime } from "ably";
import { LiveObjects } from "ably/liveobjects";

const DEFAULT_CLIENT_PREFIX = "biyo-order-client";

class LiveObjectsService {
  constructor() {
    this._client = null;
    this._maps = {};
    this._initializing = null;
    this._context = null;
    this._isReady = false;
  }

  async initialize({ apiKey, storeId, storeSubdomain, clientId }) {
    if (!apiKey) {
      console.warn(
        "[LiveObjects] Missing Ably API key; realtime sync disabled."
      );
      return;
    }
    if (!storeId) {
      console.warn("[LiveObjects] Missing storeId; skipping initialization.");
      return;
    }

    const normalizedSubdomain = (storeSubdomain || "store").toLowerCase();

    const previousContext = this._context;
    this._context = {
      apiKey,
      storeId,
      storeSubdomain: normalizedSubdomain,
      clientId: clientId || `${DEFAULT_CLIENT_PREFIX}-${Date.now()}`
    };

    if (previousContext && previousContext.storeId !== storeId) {
      return;
    }

    if (
      this._isReady &&
      previousContext &&
      previousContext.storeId === storeId
    ) {
      return;
    }

    if (this._initializing) {
      return this._initializing;
    }

    this._initializing = this._bootClient()
      .then(() => {
        this._isReady = true;
        // One-glance sync diagnostic: this must print the SAME channel as the
        // matching log in biyowebpos, or the two apps are not talking at all.
        console.log(
          `[LiveObjects] ready — orders channel: ${this._getChannelName(
            "orders"
          )} clientId: ${this.getClientId()}`
        );
      })
      .catch((error) => {
        console.error("[LiveObjects] ❌ Failed to initialize", error);
      })
      .finally(() => {
        this._initializing = null;
      });

    return this._initializing;
  }

  async _bootClient() {
    if (this._client) {
      return;
    }

    const { apiKey, clientId } = this._context;

    this._client = new Realtime({
      key: apiKey,
      clientId,
      autoConnect: true,
      plugins: { LiveObjects }
    });

    this._client.connection.on("connected", () => {});
    this._client.connection.on("failed", (err) => {
      console.error("❌ [LiveObjects] Connection failed:", err);
    });
    this._client.connection.on("disconnected", () => {});
  }

  _getChannelName(type) {
    const { storeId, storeSubdomain } = this._context;
    if (!storeId) return null;
    if (type === "product_86d")
      return `product_86d:${storeSubdomain}-${storeId}`;
    return `${storeSubdomain}-${storeId}-${type}`;
  }

  async _ensureMap(type) {
    if (!this._client || !this._context) {
      console.warn(
        `[LiveObjects] Cannot ensure map for "${type}" - client or context not available`
      );
      return null;
    }
    const channelName = this._getChannelName(type);
    if (!channelName) {
      console.warn(
        `[LiveObjects] Cannot ensure map for "${type}" - channel name is null`
      );
      return null;
    }

    if (this._maps[channelName]) {
      return this._maps[channelName];
    }

    try {
      const channelOptions = {
        modes: [
          "publish",
          "subscribe",
          "presence",
          "object_subscribe",
          "object_publish"
        ]
      };
      const channel = this._client.channels.get(channelName, channelOptions);
      const map = await channel.object.get();
      this._maps[channelName] = map;
      return map;
    } catch (error) {
      console.error(`[LiveObjects] Failed to get map for ${type}`, error);
      return null;
    }
  }

  async _ensureOrdersMap() {
    return this._ensureMap("orders");
  }

  _buildProduct86dMapKey(payload) {
    if (!payload || payload.product_id == null) return null;
    if (
      payload.modifier_option_id != null &&
      payload.modifier_option_id !== ""
    ) {
      return `pm:${payload.product_id}:${payload.modifier_option_id}`;
    }
    return String(payload.product_id);
  }

  _parseProduct86dMapKey(key) {
    const value = String(key || "");
    if (value.startsWith("pm:")) {
      const parts = value.split(":");
      return {
        product_id: parts[1],
        modifier_option_id: parts[2],
        is_86d: false
      };
    }
    return { product_id: value, modifier_option_id: null, is_86d: false };
  }

  _unwrapLiveNodeValue(node) {
    if (!node) return null;
    return typeof node.value === "function" ? node.value() : node;
  }

  _normalizeMapSnapshotEntries(snapshot) {
    if (!snapshot) return [];
    if (snapshot instanceof Map) {
      return Array.from(snapshot.entries())
        .map(([key, value]) => ({
          ...this._parseProduct86dMapKey(key),
          ...this._unwrapLiveNodeValue(value)
        }))
        .filter(Boolean);
    }
    if (typeof snapshot === "object") {
      return Object.entries(snapshot)
        .map(([key, value]) => ({
          ...this._parseProduct86dMapKey(key),
          ...this._unwrapLiveNodeValue(value)
        }))
        .filter(Boolean);
    }
    return [];
  }

  async _ensureProduct86dMap() {
    return this._ensureMap("product_86d");
  }

  async getAllProduct86dSnapshot() {
    const map = await this._ensureProduct86dMap();
    if (!map) return [];
    try {
      if (typeof map.value === "function") {
        const entries = this._normalizeMapSnapshotEntries(map.value());
        if (entries.length > 0) return entries;
      }

      if (typeof map.entries === "function") {
        const entries = Array.from(map.entries())
          .map(([key, value]) => ({
            ...this._parseProduct86dMapKey(key),
            ...this._unwrapLiveNodeValue(value)
          }))
          .filter(Boolean);
        if (entries.length > 0) return entries;
      }
    } catch (error) {
      console.error("[LiveObjects] Failed to read product 86d snapshot", error);
    }
    return [];
  }

  async subscribeToProduct86dSet(callback) {
    const map = await this._ensureProduct86dMap();
    if (!map) return () => {};

    const listener = (event) => {
      const op = event.message?.operation;
      if (!op || op.action !== "map.set" || !op.mapOp) return;
      const key = op.mapOp.key;
      const entry = map.get(key);
      const data = entry ? entry.value() : null;
      if (data && typeof callback === "function") {
        callback({
          data: { ...this._parseProduct86dMapKey(key), ...data },
          key
        });
      }
    };

    try {
      const subscription = await map.subscribe(listener);
      return () => subscription.unsubscribe();
    } catch (e) {
      console.error(
        "[LiveObjects] Failed to subscribe to product 86d updates",
        e
      );
      return () => {};
    }
  }

  async subscribeToProduct86dRemove(callback) {
    const map = await this._ensureProduct86dMap();
    if (!map) return () => {};

    const listener = (event) => {
      const op = event.message?.operation;
      if (!op || op.action !== "map.remove" || !op.mapOp) return;
      const key = op.mapOp.key;
      if (typeof callback === "function") {
        callback({ data: this._parseProduct86dMapKey(key), key });
      }
    };

    try {
      const subscription = await map.subscribe(listener);
      return () => subscription.unsubscribe();
    } catch (e) {
      console.error(
        "[LiveObjects] Failed to subscribe to product 86d removals",
        e
      );
      return () => {};
    }
  }

  async publishOrderUpdate(payload) {
    const map = await this._ensureOrdersMap();
    if (!map || !payload) return false;

    const key = (payload.id || payload.local_id || "").toString();
    if (!key) {
      console.warn("[LiveObjects] Cannot publish order update - no ID found");
      return false;
    }

    try {
      await map.set(key, payload);
      return true;
    } catch (error) {
      console.error("[LiveObjects] ❌ Failed to publish order update", error);
      return false;
    }
  }

  async subscribeToOrderUpdates(callback) {
    const map = await this._ensureOrdersMap();
    if (!map) return () => {};

    const listener = (event) => {
      const op = event.message?.operation;
      if (op && op.action === "map.set" && op.mapOp) {
        const key = op.mapOp.key;
        const data = map.get(key).value();

        if (typeof callback === "function") {
          callback({ data, key });
        }
      }
    };

    try {
      const subscription = await map.subscribe(listener);
      return () => {
        subscription.unsubscribe();
      };
    } catch (e) {
      console.error("[LiveObjects] Failed to subscribe to order updates", e);
      return () => {};
    }
  }

  async getOrderFromMap(orderId) {
    const map = await this._ensureOrdersMap();
    if (!map || !orderId) return null;

    try {
      const key = orderId.toString();
      const entry = map.get(key);
      if (entry) {
        return entry.value();
      }
      return null;
    } catch (error) {
      console.error(
        `[LiveObjects] Failed to get order ${orderId} from map`,
        error
      );
      return null;
    }
  }

  isReady() {
    return this._isReady;
  }

  getClientId() {
    if (this._client && this._client.auth) {
      return this._client.auth.clientId;
    }
    return this._context?.clientId || null;
  }

  getContext() {
    return this._context;
  }

  _teardown() {
    if (this._client) {
      try {
        this._client.close();
      } catch (error) {
        console.warn("[LiveObjects] Failed to close client", error);
      }
    }
    this._client = null;
    this._maps = {};
    this._isReady = false;
  }

  cleanup() {
    this._teardown();
    this._context = null;
  }
}

const liveObjectsService = new LiveObjectsService();
export default liveObjectsService;
